import { z } from 'zod';
import { DatasetDraftSchema } from './dataset.js';
import { ReportDefinitionSchema } from './report.js';
import { ReportOperationSchema } from './operations.js';
import { LIMITS } from './limits.js';
/**
 * The application-service port.
 *
 * `apps/server` implements this once. HTTP route handlers and MCP tools are
 * both thin adapters over it — neither contains business rules, and neither can
 * drift from the other. `packages/mcp` depends on this interface only, never on
 * the database.
 */
// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------
/** Who is making the call. The pilot is a single workspace with one admin, so
 *  this carries no tenancy — but every service method takes it, so adding
 *  tenancy later is a change to this type and its checks, not a rewrite. */
/** The single workspace every install starts with (path doc §12, Phase 1).
 *
 *  A constant rather than a generated id so a fresh install and an upgraded
 *  one agree, and so fixtures do not have to discover it at runtime. Phase 1
 *  creates exactly one workspace; nothing here assumes there will only ever
 *  be one. */
export const DEFAULT_WORKSPACE_ID = 'ws_default';
/** The single admin principal every install starts with, following
 *  `DEFAULT_WORKSPACE_ID`: a constant so a fresh install and an upgraded one
 *  agree, and so fixtures do not have to discover it at runtime. */
export const ADMIN_PRINCIPAL_ID = 'usr_admin';
/** Maps a principal kind onto the stored vocabulary.
 *
 *  A bearer token is reported as API_CLIENT because an MCP agent using a token
 *  and a CI client are indistinguishable at this layer. An OAuth agent
 *  connection is an agent acting for a person, so it is AGENT. */
export function actorTypeOf(principal) {
    if (principal.kind === 'session')
        return 'USER';
    if (principal.kind === 'connection')
        return 'AGENT';
    return 'API_CLIENT';
}
// ---------------------------------------------------------------------------
// Request shapes (shared by HTTP and MCP)
// ---------------------------------------------------------------------------
const idRef = z.string().min(1).max(64);
const requestIdRef = z
    .string()
    .min(8)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/, 'Request ids may contain letters, digits and . _ : -');
export const CreateDatasetInputSchema = z.object({
    dataset: DatasetDraftSchema,
    requestId: requestIdRef.optional()
});
const fileUrl = z.string().min(1).max(2048);
const sheetName = z.string().min(1).max(31); // Excel's sheet-name limit
/** A file to ingest: `{ url }` or ChatGPT's `{ download_url, ... }`. Shape only;
 *  the exactly-one-of rule (rows / csv / file) lives in the service so the MCP
 *  adapter can still spread `.shape`. */
export const SnapshotFileSchema = z.union([
    z.object({ url: fileUrl, sheet: sheetName.optional() }).strict(),
    z
        .object({
        download_url: fileUrl,
        file_id: z.string().max(200).optional(),
        file_name: z.string().max(255).optional(),
        mime_type: z.string().max(200).optional(),
        sheet: sheetName.optional()
    })
        .strict()
]);
export const SubmitSnapshotInputSchema = z.object({
    datasetId: idRef,
    /** Row objects keyed by dataset column id. Exactly one of rows, csv, file or linked. */
    rows: z.array(z.record(z.string(), z.unknown())).max(LIMITS.maxSnapshotRows).optional(),
    /** CSV text with a header row. Exactly one of rows, csv, file or linked. */
    csv: z.string().max(LIMITS.maxBodyBytes).optional(),
    /** A file to fetch and ingest. Exactly one of rows, csv, file or linked. */
    file: SnapshotFileSchema.optional(),
    /** Pull the dataset's linked Google Sheet. Exactly one of rows, csv, file or linked. */
    linked: z.literal(true).optional(),
    /** The caller's description of where this data came from. Stored verbatim
     *  and displayed as a claim, never verified. */
    sourceDescription: z.string().max(1000).optional(),
    /** When the caller observed the data, if known. Also a claim. */
    sourceObservedAt: z.string().max(64).optional(),
    requestId: requestIdRef.optional()
});
export const CreateReportInputSchema = z.object({
    datasetId: idRef,
    title: z.string().min(1).max(LIMITS.maxLabelChars),
    /** Snapshot to start on. Defaults to the dataset's newest snapshot. */
    snapshotId: idRef.optional(),
    /** Full starting definition. Omit to get a generated starter layout. */
    definition: ReportDefinitionSchema.optional(),
    requestId: requestIdRef.optional()
});
export const UpdateReportInputSchema = z.object({
    reportId: idRef,
    /** The revision the caller last read. A mismatch is a conflict, never an
     *  overwrite — see `REVISION_CONFLICT`. */
    expectedRevision: z.number().int().positive(),
    operations: z.array(ReportOperationSchema).min(1).max(100)
});
export const RefreshReportInputSchema = z.object({
    reportId: idRef,
    /** The active snapshot the caller last saw; `null` for a report that has
     *  never been refreshed. Guards against an older refresh racing past a newer
     *  one — see `SNAPSHOT_CONFLICT`. */
    expectedActiveSnapshotId: idRef.nullable(),
    /** Snapshot to move to. Defaults to the dataset's newest snapshot. */
    snapshotId: idRef.optional()
});
export const RestoreReportRevisionInputSchema = z.object({
    reportId: idRef,
    /** The retained revision to restore. */
    revision: z.number().int().positive(),
    /** The revision the caller last read. A mismatch is a conflict, same as
     *  `updateReport` — see `REVISION_CONFLICT`. */
    expectedRevision: z.number().int().positive()
});
export const ListReportActivityInputSchema = z.object({
    reportId: idRef,
    /** An event id, exclusive. Omit for the newest page. */
    before: z.number().int().positive().optional()
});
export const EXPORT_FORMATS = ['csv', 'pdf'];
export const ExportReportInputSchema = z.object({
    reportId: idRef,
    format: z.enum(EXPORT_FORMATS),
    /** CSV only. `dataset` writes every column of the active snapshot; `table`
     *  writes exactly the columns of one table widget, as displayed. */
    scope: z
        .discriminatedUnion('kind', [
        z.object({ kind: z.literal('dataset') }),
        z.object({ kind: z.literal('table'), widgetId: idRef })
    ])
        .optional(),
    /** Pin the export to a specific snapshot. Defaults to the report's active
     *  snapshot, captured once at the start of the export. */
    snapshotId: idRef.optional()
});
export const PublishInputSchema = z.object({
    reportId: idRef,
    expectedRevision: z.number().int().positive().optional()
});
export const SetAutoUpdateInputSchema = z.object({
    reportId: idRef,
    autoUpdate: z.boolean()
});
export const RemixInputSchema = z.object({ workspaceId: z.string().min(1) });
//# sourceMappingURL=service.js.map